/**
 * StickmanRenderer - 火柴人渲染器
 * @description 使用 Phaser Graphics 绘制简线条风格的火柴人
 */

import Phaser from 'phaser';
import { StickmanBody, DEFAULT_STICKMAN_CONFIG } from './StickmanBody.ts';
import type { StickmanPartId } from './StickmanBody.ts';
import { Vec2 } from 'planck';

/** 渲染颜色配置 */
export interface StickmanColors {
  /** 线条颜色 */
  line: number;
  /** 头部填充颜色 */
  headFill: number;
  /** 线条宽度 */
  lineWidth: number;
}

/** 默认颜色配置（简线条风格） */
const DEFAULT_COLORS: StickmanColors = {
  line: 0xffffff,     // 白色线条
  headFill: 0x1a1a2e, // 与背景相近的填充色
  lineWidth: 3,
};

/**
 * StickmanRenderer 类
 * 负责将火柴人物理骨骼渲染为简线条图形
 */
export class StickmanRenderer {
  private graphics: Phaser.GameObjects.Graphics;
  private body: StickmanBody;
  private colors: StickmanColors;
  private scale: number;
  private offsetY: number;
  private partConfigs: Map<string, { radius?: number; width?: number; height?: number }> = new Map();
  private armHalfLength: number = 0;
  private legHalfLength: number = 0;
  private torsoHalfHeight: number = 0;
  private torsoWidth: number = 0;
  private headRadius: number = 0;

  /**
   * 创建火柴人渲染器
   * @param scene Phaser 场景实例
   * @param body 火柴人物理骨骼
   * @param colors 颜色配置（可选）
   * @param scale 渲染缩放因子，默认 1
   * @param offsetY y 轴偏移（用于坐标系转换），默认 600（屏幕高度）
   */
  constructor(
    _scene: Phaser.Scene,
    body: StickmanBody,
    colors?: StickmanColors,
    scale: number = 1,
    offsetY: number = 600
  ) {
    this.body = body;
    this.colors = colors ?? DEFAULT_COLORS;
    this.scale = scale;
    this.offsetY = offsetY;

    this.graphics = _scene.add.graphics();
    this.graphics.setDepth(10);

    this.cachePartConfigs();
  }

  private cachePartConfigs(): void {
    for (const part of DEFAULT_STICKMAN_CONFIG.parts) {
      this.partConfigs.set(part.id, part.size);
    }
    this.headRadius = (this.partConfigs.get('head')?.radius ?? 15) * this.scale;
    this.torsoHalfHeight = (this.partConfigs.get('torso')?.height ?? 40) * this.scale / 2;
    this.torsoWidth = (this.partConfigs.get('torso')?.width ?? 20) * this.scale;
    this.armHalfLength = (this.partConfigs.get('upperArmL')?.height ?? 25) * this.scale / 2;
    this.legHalfLength = (this.partConfigs.get('upperLegL')?.height ?? 30) * this.scale / 2;
  }

  /**
   * 更新渲染（每帧调用）
   * 从物理骨骼获取位置和角度，绘制简线条火柴人
   */
  render(): void {
    this.graphics.clear();
    this.graphics.lineStyle(this.colors.lineWidth, this.colors.line);

    const positions = this.body.getAllPositions();
    const angles = this.body.getAllAngles();

    this.drawHead(positions, angles);
    this.drawTorso(positions, angles);
    this.drawArms(positions, angles);
    this.drawLegs(positions, angles);
  }

  /**
   * 绘制头部（圆形）
   */
  private drawHead(
    positions: Map<StickmanPartId, Vec2>,
    angles: Map<StickmanPartId, number>
  ): void {
    const headPos = positions.get('head');
    const headAngle = angles.get('head') ?? 0;

    if (!headPos) return;

    const screenPos = this.toScreenCoords(headPos);

    this.graphics.save();
    this.graphics.translateCanvas(screenPos.x, screenPos.y);
    this.graphics.rotateCanvas(-headAngle);

    this.graphics.fillStyle(this.colors.headFill);
    this.graphics.fillCircle(0, 0, this.headRadius);
    this.graphics.strokeCircle(0, 0, this.headRadius);

    this.graphics.restore();
  }

  /**
   * 绘制躯干（简线条，连接头部到臀部）
   */
  private drawTorso(
    positions: Map<StickmanPartId, Vec2>,
    _angles: Map<StickmanPartId, number>
  ): void {
    const headPos = positions.get('head');
    const torsoPos = positions.get('torso');

    if (!headPos || !torsoPos) return;

    const headScreen = this.toScreenCoords(headPos);
    const torsoScreen = this.toScreenCoords(torsoPos);

    this.graphics.beginPath();
    this.graphics.moveTo(headScreen.x, headScreen.y + this.headRadius);

    const hipY = torsoScreen.y + this.torsoHalfHeight * (this.offsetY > 300 ? 1 : -1);
    this.graphics.lineTo(torsoScreen.x, hipY);
    this.graphics.strokePath();
  }

  /**
   * 绘制手臂（简线条）
   */
  private drawArms(
    positions: Map<StickmanPartId, Vec2>,
    angles: Map<StickmanPartId, number>
  ): void {
    const torsoPos = positions.get('torso');
    const upperArmL = positions.get('upperArmL');
    const upperArmR = positions.get('upperArmR');
    const lowerArmL = positions.get('lowerArmL');
    const lowerArmR = positions.get('lowerArmR');

    if (!torsoPos) return;

    const torsoScreen = this.toScreenCoords(torsoPos);
    const shoulderY = torsoScreen.y - this.torsoHalfHeight;

    this.graphics.beginPath();

    if (upperArmL && lowerArmL) {
      const upperArmLScreen = this.toScreenCoords(upperArmL);
      const lowerArmLScreen = this.toScreenCoords(lowerArmL);

      const elbowL = this.calculateEndpoint(upperArmLScreen, angles.get('upperArmL') ?? 0, -this.armHalfLength);
      const handL = this.calculateEndpoint(lowerArmLScreen, angles.get('lowerArmL') ?? 0, -this.armHalfLength);

      this.graphics.moveTo(torsoScreen.x - this.torsoWidth / 2, shoulderY);
      this.graphics.lineTo(elbowL.x, elbowL.y);
      this.graphics.lineTo(handL.x, handL.y);
    }

    if (upperArmR && lowerArmR) {
      const upperArmRScreen = this.toScreenCoords(upperArmR);
      const lowerArmRScreen = this.toScreenCoords(lowerArmR);

      const elbowR = this.calculateEndpoint(upperArmRScreen, angles.get('upperArmR') ?? 0, -this.armHalfLength);
      const handR = this.calculateEndpoint(lowerArmRScreen, angles.get('lowerArmR') ?? 0, -this.armHalfLength);

      this.graphics.moveTo(torsoScreen.x + this.torsoWidth / 2, shoulderY);
      this.graphics.lineTo(elbowR.x, elbowR.y);
      this.graphics.lineTo(handR.x, handR.y);
    }

    this.graphics.strokePath();
  }

  /**
   * 绘制腿部（简线条）
   */
  private drawLegs(
    positions: Map<StickmanPartId, Vec2>,
    angles: Map<StickmanPartId, number>
  ): void {
    const torsoPos = positions.get('torso');
    const upperLegL = positions.get('upperLegL');
    const upperLegR = positions.get('upperLegR');
    const lowerLegL = positions.get('lowerLegL');
    const lowerLegR = positions.get('lowerLegR');

    if (!torsoPos) return;

    const torsoScreen = this.toScreenCoords(torsoPos);
    const hipY = torsoScreen.y + this.torsoHalfHeight;

    this.graphics.beginPath();

    if (upperLegL && lowerLegL) {
      const upperLegLScreen = this.toScreenCoords(upperLegL);
      const lowerLegLScreen = this.toScreenCoords(lowerLegL);

      const kneeL = this.calculateEndpoint(upperLegLScreen, angles.get('upperLegL') ?? 0, -this.legHalfLength);
      const footL = this.calculateEndpoint(lowerLegLScreen, angles.get('lowerLegL') ?? 0, -this.legHalfLength);

      this.graphics.moveTo(torsoScreen.x - 8 * this.scale, hipY);
      this.graphics.lineTo(kneeL.x, kneeL.y);
      this.graphics.lineTo(footL.x, footL.y);
    }

    if (upperLegR && lowerLegR) {
      const upperLegRScreen = this.toScreenCoords(upperLegR);
      const lowerLegRScreen = this.toScreenCoords(lowerLegR);

      const kneeR = this.calculateEndpoint(upperLegRScreen, angles.get('upperLegR') ?? 0, -this.legHalfLength);
      const footR = this.calculateEndpoint(lowerLegRScreen, angles.get('lowerLegR') ?? 0, -this.legHalfLength);

      this.graphics.moveTo(torsoScreen.x + 8 * this.scale, hipY);
      this.graphics.lineTo(kneeR.x, kneeR.y);
      this.graphics.lineTo(footR.x, footR.y);
    }

    this.graphics.strokePath();
  }

  /**
   * 转换物理坐标到屏幕坐标
   * Planck y 轴向上 -> Phaser y 轴向下
   */
  private toScreenCoords(pos: Vec2): { x: number; y: number } {
    return {
      x: pos.x * this.scale,
      y: this.offsetY - pos.y * this.scale,
    };
  }

  /**
   * 计算肢体末端位置（基于角度）
   */
  private calculateEndpoint(
    center: { x: number; y: number },
    angle: number,
    length: number
  ): { x: number; y: number } {
    return {
      x: center.x + Math.sin(angle) * length,
      y: center.y + Math.cos(angle) * length,
    };
  }

  /**
   * 获取 Graphics 对象（用于交互检测）
   */
  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  /**
   * 设置颜色配置
   */
  setColors(colors: StickmanColors): void {
    this.colors = colors;
  }

  /**
   * 销毁渲染器
   */
  destroy(): void {
    this.graphics.destroy();
  }

  /**
   * 获取头部屏幕位置（用于拖拽检测）
   */
  getHeadScreenPosition(): { x: number; y: number } {
    const headPos = this.body.getHeadPosition();
    return this.toScreenCoords(headPos);
  }
}